import { useState, useRef, useEffect, lazy } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { Navbar } from "./components/navbar/navbar";
import { Footer } from "./components/footer/footer";
import { LightParticles } from "./components/light-particles/light-particles";
import { debounce } from "./utils/debounce";
import { CartDrawer } from "./components/cart-drawer/cart-drawer";
import { CartProvider } from "./context/cart/cart-context.tsx";
import { SkeletonTheme } from "react-loading-skeleton";
import 'react-loading-skeleton/dist/skeleton.css';
import { ScrollToTop } from "./components/scroll-to-top/scroll-to-top.tsx";

const Home = lazy(() => import("./pages/home/home"));
const Categories = lazy(() => import("./pages/categories/categories"));
const Products = lazy(() => import("./pages/products/products"));
const PackageInfo = lazy(() => import("./pages/package-info/package-info"));
const Terms = lazy(() => import("./pages/terms/terms.tsx"));
const NotFound = lazy(() => import("./pages/not-found/not-found.tsx"));

function AppContent() {
  const [navHeight, setNavHeight] = useState<number>(0);
  const navRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    if (!navRef.current) return;
    const lastHeight = { current: 0 };

    const updateHeight = (entry?: ResizeObserverEntry) => {
      const newHeight = entry
        ? Math.round(entry.contentRect.height)
        : Math.round(navRef.current?.offsetHeight ?? 0);

      if (Math.abs(lastHeight.current - newHeight) < 2) return;
      lastHeight.current = newHeight;
      setNavHeight(newHeight);
      document.documentElement.style.setProperty("--nav-height", `${newHeight}px`);
    };

    updateHeight();

    const observer = new ResizeObserver(debounce(([entry]) => updateHeight(entry), 80));
    observer.observe(navRef.current);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      const height = navRef.current?.offsetHeight ?? 0;
      setNavHeight(height);
      document.documentElement.style.setProperty("--nav-height", `${height}px`);
    }, 100);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  return (
    <div style={{ position: "relative", zIndex: 1 }}>
      <ScrollToTop />
      <LightParticles />
      <Navbar ref={navRef} />

      <main style={{ paddingTop: navHeight || 72 }}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/categorias" element={<Categories />} />
          <Route path="/produtos" element={<Products />} />
          <Route path="/package-info/:slug" element={<PackageInfo />} />
          <Route path="/termos" element={<Terms />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>

      <CartDrawer />
      <Footer />
    </div>
  );
}

function App() {
  return (
    <CartProvider>
      <SkeletonTheme
        baseColor="#1b1b1b"
        highlightColor="#404040"
        borderRadius="1rem"
        duration={1.2}
      >
        <AppContent />
      </SkeletonTheme>
    </CartProvider>
  );
}

export default App;
